import type { OrganizationFolder } from '../types';

interface FolderNoteMutations {
  addNoteToFolder: (args: { organizationId: string; folderId: string; noteId: string }) => Promise<unknown>;
  removeNoteFromFolder: (args: { organizationId: string; folderId: string; noteId: string }) => Promise<unknown>;
}

/**
 * Move a note into `targetFolderId` ('general' = no folder), reconciling its
 * current folder membership: remove it from every other folder it's filed in,
 * then add it to the target (unless General). Safe to call when the note is
 * already in the target. Throws if any removal fails so the add is skipped and
 * the note is never left in two folders simultaneously.
 */
export const reconcileNoteFolder = async (
  folders: OrganizationFolder[],
  organizationId: string,
  noteId: string,
  targetFolderId: string,
  { addNoteToFolder, removeNoteFromFolder }: FolderNoteMutations,
): Promise<void> => {
  const toRemove = folders.filter((f) => f.note_ids.includes(noteId) && f.id !== targetFolderId);
  const results = await Promise.allSettled(
    toRemove.map((f) => removeNoteFromFolder({ organizationId, folderId: f.id, noteId })),
  );
  if (results.some((r) => r.status === 'rejected')) {
    throw new Error('Failed to remove note from prior folder');
  }

  const target = folders.find((f) => f.id === targetFolderId);
  if (targetFolderId !== 'general' && target && !target.note_ids.includes(noteId)) {
    await addNoteToFolder({ organizationId, folderId: targetFolderId, noteId });
  }
};
